import { API } from "@swivl-mono/api-helper";
import { warn } from "@swivl-mono/helpers";
import {
  AccountInterface,
  AppType,
  LoginDataInterface,
  MemberInterface,
  MemberInviteInterface,
  MemberInviteStatus,
  SocketEventType,
} from "@swivl-mono/types";
import { Agent } from "http";
import _ from "lodash";
import { NavigateFunction } from "react-router-dom";
import { create } from "zustand";

import AppsModel from "../Apps/Apps.Model";
import { LocalStorageHelper } from "../LocalStorage/LocalStorageHelper";
import { Member } from "../Member/Member.Current";
import MembersModel from "../Member/Members.Model";
import { AlertsModel } from "../Notifications/Alerts.Model";
import NotificationsModel from "../Notifications/Notifications.Model";
import RolesModel from "../Roles/Roles.Model";
import SocketEventsManager from "../SocketEvents/SocketEvents.Manager";
import { TopicVersionsManager } from "../TopicVersions/TopicVersions.Manager";
import { UsersModel } from "../Users/Users.Model";
import LocationVarsModel from "../Variables/LocationVars.Model";

interface StoreInterface {
  account?: AccountInterface;
  memberships?: MemberInterface[];
  member?: MemberInterface;
  invites?: MemberInviteInterface[];
  accountLoaded?: boolean;
  accountIsLoading?: boolean; // Used to show if the account is loading, and if so show the loading screen
  loginError?: string;
}

const BlankState: StoreInterface = {
  account: undefined,
  memberships: undefined,
  member: undefined,
  accountLoaded: false,
  accountIsLoading: false,
  loginError: undefined,
};

export default class AccountModel {
  static useState = create<StoreInterface>((set) => ({ ...BlankState }));
  static setState = AccountModel.useState.setState;
  static get state() {
    return AccountModel.useState.getState();
  }
  static reset() {
    AccountModel.setState({ ...BlankState });
  }
  static isLoading = false;
  static timer = setInterval(
    () => {
      AccountModel.updateLastActive();
    },
    1000 * 60 * 5,
  ); // every 5 minutes
  static async loadAccount(force?: boolean) {
    if (!force && (AccountModel.isLoading || AccountModel.state.accountLoaded)) {
      return;
    }

    AccountModel.isLoading = true;

    const account = LocalStorageHelper.getItem("account");
    const memberships = LocalStorageHelper.getItem("memberships");
    const member = LocalStorageHelper.getItem("member");
    if (account) {
      AccountModel.setState({ account: JSON.parse(account) });
    } else {
      AccountModel.setState({ accountLoaded: true });
      AccountModel.isLoading = false;

      return;
      // if (window.location.pathname !== '/login' ||

      // window.location.pathname.includes('/invite') ||
      // window.location.pathname.includes('/reset-password') ||
      // ) {
      //   window.location.href = '/login';
      // }
    }
    if (memberships) {
      AccountModel.setState({ memberships: JSON.parse(memberships) });
    }
    if (member) {
      // Refresh the member if needed
      const parsedMember = JSON.parse(member);
      await RolesModel.loadRoles(parsedMember.orgId);

      AccountModel.setState({ member: parsedMember });

      let refreshedMember = await AccountModel.getRefreshedMemberIfNeeded(JSON.parse(member));

      if (refreshedMember) {
        AccountModel.setState({ member: refreshedMember as MemberInterface });
        if (memberships) {
          const membershipsArray = JSON.parse(memberships);
          const index = membershipsArray.findIndex((m: MemberInterface) => m.id === refreshedMember!.id);
          if (index > -1) {
            membershipsArray[index] = refreshedMember;
          }
          LocalStorageHelper.setItem("memberships", JSON.stringify(membershipsArray));
          AccountModel.setState({
            member: refreshedMember,
            memberships: membershipsArray,
          });
        } else {
          LocalStorageHelper.setItem("member", JSON.stringify(refreshedMember));
          AccountModel.setState({ member: refreshedMember });
        }
      }

      // try {
      //   await MembersModel.loadMembers();
      //   await LocationVarsModel.loadAllVariables();

      //   AccountModel.updateLastActive();
      //   UsersModel.loadUnacknowledgedEscalations();
      //   SocketEventsManager.connect(Member.id!);
      //   NotificationsModel.loadNotifications(false, true);
      // } catch (error) {
      //   warn("Error initializing system", error);
      // }
      setTimeout(() => {
        AccountModel.finishLoading(true);
      }, 500);
    } else {
      setTimeout(() => {
        AccountModel.finishLoading(false);
      }, 500);
    }
  }
  static async finishLoading(hasMember: boolean) {
    if (hasMember) {
      try {
        await MembersModel.loadMembers();
        await LocationVarsModel.loadAllVariables();

        AccountModel.updateLastActive();
        UsersModel.loadUnacknowledgedEscalations();
        SocketEventsManager.connect(Member.id!);
        NotificationsModel.loadNotifications(false, true);
        TopicVersionsManager.startPeriodicCheckForTopicVersionChanges();
      } catch (error) {
        warn("Error initializing system", error);
      }
    }
    AccountModel.setState({ accountLoaded: true });
    AccountModel.isLoading = false;
  }

  static signUp(
    firstName: string,
    lastName: string,
    email: string,
    avatarURL: string,
    password: string,
    inviteUUID?: string,
    navigate?: NavigateFunction,
  ) {
    return API.Account.signUp(firstName, lastName, email, avatarURL, password, inviteUUID).then((response: AccountInterface) => {
      if (!response) {
        throw new Error("No account returned");
      }
      AccountModel.logIn(email, password, navigate, true);
    });
  }

  static logIn(username: string, password: string, navigate?: NavigateFunction, refreshAfterLoging?: boolean): Promise<LoginDataInterface> {
    if (refreshAfterLoging) {
      AccountModel.setState({ accountIsLoading: true });
    }
    return API.Account.logIn(username, password)
      .then((response: LoginDataInterface) => {
        AccountModel.setState({
          account: response.account,
          memberships: response.memberships,
          invites: response.invites,
          loginError: undefined,
        });

        LocalStorageHelper.setItem("account", JSON.stringify(response.account));
        LocalStorageHelper.setItem("memberships", JSON.stringify(response.memberships));
        // if (response.memberships.length === 1) {
        //   AccountModel.setState({ account: response.account, memberships: response.memberships, member: response.memberships[0]});

        // } else {
        //   AccountModel.setState({ account: response.account, memberships: response.memberships});
        // }

        if (navigate && response.memberships.length === 0) {
          navigate("/login");
          return response;
        }
        if (response.memberships.length === 1) {
          AccountModel.setState({
            member: response.memberships[0],
          });
          LocalStorageHelper.setItem("member", JSON.stringify(response.memberships[0]));
        }
        if (navigate && response.memberships.length > 1) {
          AccountModel.setState({ accountIsLoading: false });
          if (response.invites.length > 0) {
            navigate("/invites");
            return response;
          }
          navigate("/select-org");
          return response;
        }
        if (refreshAfterLoging) {
          if (navigate) {
            navigate("/dashboard");
          }
          window.location.reload();
        } else {
          if (navigate) {
            navigate("/dashboard");
            return response;
          }
        }

        return response;
      })
      .catch((error) => {
        warn("Error logging in", error);
        AccountModel.setState({ accountIsLoading: false, loginError: "Unable to find account with that email or password" });
        return error;
      });
  }
  static updatePassword(currentPassword: string, newPassword: string) {
    return API.Account.updatePassword(AccountModel.state.account!.id, currentPassword, newPassword).then((response: AccountInterface) => {
      if (!response) {
        throw new Error("No account returned");
      }
      AccountModel.setState({
        account: response,
      });
      LocalStorageHelper.setItem("account", JSON.stringify(response));
    });
  }

  static async selectMember(member: MemberInterface) {
    LocalStorageHelper.setItem("member", JSON.stringify(member));
    await AccountModel.loadAccount(true);
  }
  static async clearMember() {
    LocalStorageHelper.removeItem("member");
    // AccountModel.setState({member:undefined})
    //reload the page
    window.location.reload();
  }

  static loadInvite(inviteUUID: string) {
    return API.MemberInvite.loadInvite(inviteUUID).then((invite) => {
      let invites = AccountModel.state.invites ? AccountModel.state.invites : [];
      invites.push(invite);
      AccountModel.setState({ invites: invites });
    });
  }

  static logOut() {
    LocalStorageHelper.removeItem("account");
    LocalStorageHelper.removeItem("memberships");
    LocalStorageHelper.removeItem("member");

    window.location.href = "/login";

    AccountModel.reset();
  }

  static async getRefreshedMemberIfNeeded(member?: MemberInterface): Promise<MemberInterface | undefined> {
    if (!member) {
      warn("No member to refresh");

      return;
    }

    const response = await API.Account.refreshMember(member.orgId, member.id).catch((error) => {
      warn("Error:", error);
    });

    if (!response) {
      warn("$$ No response from server");
      return;
    }
    if (!response.permissions) {
      return;
    }
    if (_.isEqual(response.permissions, member.permissions)) {
      return;
    } else {
      return response;
      // AccountModel.setState({ member: response });
      // LocalStorageHelper.setItem("member", JSON.stringify(response));
    }
  }

  static async forgotPassword(email: string) {
    return API.Account.forgotPassword(email);
  }

  static async resetPassword(token: string, password: string, navigate: NavigateFunction) {
    return API.Account.resetPassword(token, password).then((account: AccountInterface) => {
      if (account && account.email) {
        AlertsModel.successToast("Password reset successfully");
        AccountModel.logIn(account.email, password, navigate, true);
      }
    });
  }

  static async acceptInvite(inviteId: number) {
    let invites = AccountModel.state.invites ? AccountModel.state.invites : [];
    const index = invites.findIndex((i) => i.id === inviteId);
    if (index === -1) {
      warn("Invite not found");
      return;
    }

    const invite = invites[index];
    try {
      const response: {
        member: MemberInterface;
        invite: MemberInviteInterface;
      } = await API.MemberInvite.acceptInvite(invite, Member.account!.id);

      if (response) {
        let memberships = AccountModel.state.memberships ? AccountModel.state.memberships : [];
        memberships.push(response.member);
        AccountModel.setState({ memberships: memberships });
        LocalStorageHelper.setItem("memberships", JSON.stringify(memberships));
        invites[index].status = MemberInviteStatus.ACCEPTED;
        AccountModel.setState({ invites: invites });
      } else {
        warn("Error accepting invite mk2");
        AlertsModel.errorToast("Error accepting invite");
      }
    } catch (error) {
      warn("Error accepting invite", error);
      AlertsModel.errorToast("Error accepting invite");
      return;
    }
  }

  static async denyInvite(inviteId: number) {
    let invites = AccountModel.state.invites ? AccountModel.state.invites : [];
    const index = invites.findIndex((i) => i.id === inviteId);
    if (index === -1) {
      warn("Invite not found");
      return;
    }

    invites[index].status = MemberInviteStatus.DENIED;
    AccountModel.setState({ invites: invites });
    const invite = invites[index];
    return API.MemberInvite.denyOrRevokeInvite(invite.orgId, invite.uuid!, MemberInviteStatus.DENIED).catch((error) => {
      warn("Error denying invite", error);
      AlertsModel.errorToast("Error denying invite");
    });
  }

  static updateLastActive() {
    if (Member.member) {
      API.Members.updateMemberLastActive(Member.orgId, Member.member.id);
    }
  }
}
